import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Ticket, Sparkles, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePlayer } from "@/context/PlayerContext";
import { betaRedeem } from "@/lib/api";
import { toast } from "sonner";
import { playWin } from "@/lib/sound";

/**
 * Small self-contained card that lets a player enter a beta code and receive
 * bonus memory shards. Meant to be dropped in /parental or /profile.
 */
export default function BetaCodeInput() {
  const { t } = useTranslation();
  const { player, setPlayer } = usePlayer();
  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!player) return null;
  const already = (player.redeemed_beta_codes || []).length > 0 || player.beta_tester;

  const submit = async (e) => {
    e.preventDefault();
    const c = code.trim().toUpperCase();
    if (!c) { toast.error(t("beta.err_empty")); return; }
    setSubmitting(true);
    try {
      const updated = await betaRedeem(c, player.id);
      setPlayer(updated);
      const gained = (updated.memory_shards || 0) - (player.memory_shards || 0);
      toast.success(t("beta.success", { n: gained }));
      playWin();
      setDone(true);
      setCode("");
    } catch (err) {
      const status = err?.response?.status;
      const key = status === 404 ? "err_notfound"
                : status === 409 ? "err_used"
                : status === 429 ? "err_rate_limit"
                : "err_generic";
      toast.error(t(`beta.${key}`));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="glass rounded-3xl p-6 mt-6" data-testid="beta-code-section">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-2xl glass flex items-center justify-center text-fuchsia-300 flex-shrink-0">
          <Ticket className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-[0.3em] text-fuchsia-300 font-bold">{t("beta.kicker")}</div>
          <h3 className="mt-1 font-serif text-xl font-bold text-stone-50">{t("beta.title")}</h3>
          <p className="mt-1 text-sm text-stone-300">{t("beta.desc")}</p>

          {done || already ? (
            <div className="mt-4 inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-emerald-200 text-sm" data-testid="beta-code-done">
              <Check className="w-4 h-4 text-emerald-300" />
              {t("beta.already_done")}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-4 flex flex-col sm:flex-row gap-2" data-testid="beta-code-form">
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX"
                maxLength={12}
                className="flex-1 rounded-full px-5 py-3 bg-stone-950/60 border border-white/15 text-stone-100 placeholder:text-stone-500 focus:outline-none focus:border-fuchsia-400/60 uppercase tracking-widest text-center"
                data-testid="beta-code-input"
              />
              <Button
                type="submit"
                disabled={submitting}
                className="bg-fuchsia-500 hover:bg-fuchsia-400 text-stone-950 font-bold rounded-full px-6"
                data-testid="beta-code-submit"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {submitting ? t("beta.sending") : t("beta.cta")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
